import { Check, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ChecklistStep } from '@/entities/task/schemas/checklist-step';
import { CONTROL_CLASS } from '@/shared/components/form/control-class';
import { useTranslation } from '@/shared/i18n/use-translation';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button/button';
import { Input } from '@/shared/ui/input/input';

type ChecklistFieldProps = {
  readonly value: readonly ChecklistStep[];
  readonly onChange: (value: ChecklistStep[]) => void;
  /** Ticking a step; the editor saves it at once. */
  readonly onToggle: (value: ChecklistStep[]) => void;
};

/** The steps of a task: tick, rename, remove, and Enter adds the next one. */
export function ChecklistField({ value, onChange, onToggle }: ChecklistFieldProps) {
  const { t } = useTranslation();
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const [focus, setFocus] = useState<number | null>(null);

  useEffect(() => {
    if (focus === null) return;
    inputs.current[focus]?.focus();
    setFocus(null);
  }, [focus]);

  function add(at: number) {
    onChange([...value.slice(0, at), { text: '', done: false }, ...value.slice(at)]);
    setFocus(at);
  }

  function update(index: number, step: ChecklistStep) {
    onChange(value.map((current, i) => (i === index ? step : current)));
  }

  const done = value.filter((step) => step.done && step.text.trim()).length;
  const total = value.filter((step) => step.text.trim()).length;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {t('Checklist')}
          {total > 0 && <span className="ml-2 font-normal text-muted-foreground">{`${done}/${total}`}</span>}
        </span>
        <Button type="button" variant="outline" size="sm" onClick={() => add(value.length)}>
          <Plus /> {t('Add step')}
        </Button>
      </div>
      {value.length > 0 && (
        <ul className="flex flex-col gap-1">
          {value.map((step, index) => (
            // Steps have no id; the index is their identity while editing.
            // biome-ignore lint/suspicious/noArrayIndexKey: steps are plain text, edited in place
            <li key={index} className="flex items-center gap-1">
              <Button
                type="button"
                variant={step.done ? 'default' : 'outline'}
                size="icon"
                role="checkbox"
                aria-checked={step.done}
                aria-label={step.text.trim() || t('Step {number}', { number: index + 1 })}
                className="size-10 shrink-0 rounded-full md:size-9"
                onClick={() => onToggle(value.map((s, i) => (i === index ? { ...s, done: !s.done } : s)))}
              >
                <Check className={cn(!step.done && 'opacity-30')} />
              </Button>
              <Input
                ref={(element) => {
                  inputs.current[index] = element;
                }}
                value={step.text}
                placeholder={t('Step {number}', { number: index + 1 })}
                aria-label={t('Step {number}', { number: index + 1 })}
                className={cn(CONTROL_CLASS, step.done && 'text-muted-foreground line-through')}
                onChange={(event) => update(index, { ...step, text: event.target.value })}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  event.preventDefault();
                  add(index + 1);
                }}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-10 shrink-0 md:size-9"
                aria-label={t('Remove step {number}', { number: index + 1 })}
                onClick={() => onChange(value.filter((_, i) => i !== index))}
              >
                <X />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
